import TitrePage from "../components/charte/TitrePage";
import {
  Box,
  Button,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { TypeLieuComplet, getTaxonomieComplete, compterPoiParSousType, idSousTypeUnique } from "../utilities/typeLieuService";
import dayjs from "dayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

import { useBonusForm } from "../custom_forms/BonusForm";
import { loaderOff, loaderOn, logout, openSnackbar } from "../redux/actions";
import {
  addBonus,
  getActiveBonus,
  getBonus,
  getBonusId,
  updateBonus,
  updateBonusDelete,
} from "../utilities/backofficeservice";
import { getUser } from "../utilities/adminservice";
import { envoyerPushConcours, messageServeur } from "../utilities/userService";
import CommonDialog from "../commondialog/confirm";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import store from "../redux/store";
import { AIDE_RESEAU } from "../aide/textesAide";

/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  bg: 'var(--c-bg)',
  card: 'var(--c-surface)',
  border: 'var(--c-border)',
  borderAccent: '#1F1E1B',
  accent: '#1F1E1B',
  accentLight: '#57534A',
  accentMuted: 'rgba(31,30,27,0.07)',
  text: '#1F1E1B',
  textSub: '#57534A',
  textMuted: '#57534A',
  success: '#356A0F',
  warning: '#7C5600',
  error: '#A23A0D',
} as const;

const formatDateString = (dateString: any) => {
  const date = new Date(dateString);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
};

/* Un concours commencé ne se modifie plus et ne se supprime plus (D8) : le
   serveur répond 409, l'écran désactive les boutons en amont. */
const aCommence = (start: string | null | undefined) => {
  if (!start) return false;
  const t = new Date(String(start).replace(' ', 'T')).getTime();
  return !isNaN(t) && t <= Date.now();
};
const NON_MODIFIABLE = 'Concours commencé : non modifiable';
const messageConcours = (err: any, repli: string) =>
  err?.response?.status === 409
    ? "Concours commencé : le serveur refuse de le modifier ou de le supprimer."
    : (typeof err?.response?.data === 'string' && err.response.data.trim() ? err.response.data : repli);

function Countdown({ endDate }: { endDate: string }) {
  const getTimeLeft = () => {
    const diff = new Date(endDate).getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, mins: 0 };
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    return { days, hours, mins };
  };
  const [timeLeft, setTimeLeft] = useState(getTimeLeft());
  useEffect(() => {
    setTimeLeft(getTimeLeft());
    const iv = setInterval(() => setTimeLeft(getTimeLeft()), 60000);
    return () => clearInterval(iv);
  }, [endDate]);

  return (
    <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5 }}>
      {[{ label: 'Jours', value: timeLeft.days }, { label: 'Heures', value: timeLeft.hours }, { label: 'Min', value: timeLeft.mins }].map((b) => (
        <Box key={b.label} sx={{ textAlign: 'center', minWidth: 42, background: 'rgba(31,30,27,0.04)', borderRadius: '6px', py: 0.5, px: 0.75 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.text, lineHeight: 1 }}>{b.value}</Typography>
          <Typography sx={{ fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{b.label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

const fieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    background: 'rgba(31,30,27,0.03)',
    color: C.text,
    borderRadius: '8px',
    '& fieldset': { borderColor: C.border },
    '&:hover fieldset': { borderColor: C.borderAccent },
    '&.Mui-focused fieldset': { borderColor: C.accentLight },
  },
  '& .MuiInputLabel-root': { color: C.textMuted },
  '& .MuiInputLabel-root.Mui-focused': { color: C.accentLight },
};

const selectSx = {
  background: 'rgba(31,30,27,0.03)',
  color: C.text,
  borderRadius: '8px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.borderAccent },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.accentLight },
  '& .MuiSvgIcon-root': { color: C.textMuted },
};

interface ActiveRow {
  bonus_id: number;
  bonus_name: string;
  categoryDetail: string;
  no_of_tokens: string;
  raw_start_date: string;
  raw_end_date: string;
  start_date: string;
  end_date: string;
}

interface AllRow {
  bonus_id: number;
  bonus_name: string;
  categoryDetail: string;
  no_of_tokens: string;
  start_date: string;
  end_date: string;
  bonus_type: string;
  winnerEmail: string | null;
}

/* Sous-type de lieu visé : categoryDetail, ou token_category.details que le
   serveur synthétise pour les anciens concours. */
const sousTypeVise = (r: any): string => r.categoryDetail || r.token_category?.details || '';

/* Même masquage que les autres écrans : trois caractères puis le domaine. */
const masquerEmail = (email: string) => {
  const [local, domain] = email.split('@');
  return local.slice(0, 3) + '***@' + (domain || '');
};

export default function Bonus() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state: any) => state.auth.isLoggedIn);
  const isTokenExpired = useSelector((state: any) => state.auth.isTokenExpired);


  const { formValues, setFormValues } = useBonusForm();

  const [rows, setRows] = useState<ActiveRow[]>([]);
  const [rows1, setRows1] = useState<AllRow[]>([]);
  const [selectedValue1, setSelectedValue1] = useState('');
  /* Sous-types proposés : ils viennent du référentiel des types de lieu, pas
     du catalogue de tarifs. C'est le seul vocabulaire que portent les POI. */
  const [taxonomie, setTaxonomie] = useState<TypeLieuComplet[]>([]);
  const [poiCibles, setPoiCibles] = useState<number | null>(null);
  const [collecteursCibles, setCollecteursCibles] = useState(0);

  useEffect(() => {
    let vivant = true;
    getTaxonomieComplete()
      .then(t => { if (vivant) setTaxonomie(t); })
      .catch(() => { /* le sélecteur reste vide et le dit */ });
    return () => { vivant = false; };
  }, []);

  const compterPoi = async (sousType: string) => {
    setPoiCibles(null);
    const r = await compterPoiParSousType(sousType, idSousTypeUnique(taxonomie, sousType));
    setPoiCibles(r.total);
    setCollecteursCibles(r.collecteurs);
  };
  const [sendPush, setSendPush] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [bonus_write, setBonusWrite] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [histPage, setHistPage] = useState(0);
  const [sendingPush, setSendingPush] = useState<Record<number, boolean>>({});
  const [pushConfirm, setPushConfirm] = useState<Record<number, string>>({});
  const histRowsPerPage = 10;

  const resetForm = () => {
    setFormValues({
      bonus_name: { value: '', error: false, errorMessage: 'Please enter valid bonus name' },
      bonus_description: { value: '', error: false, errorMessage: 'Please enter valid bonus description' },
      bonus_criteria: { value: '', error: false, errorMessage: 'Please enter valid bonus criteria' },
      start_date: { value: '', error: false, errorMessage: 'Please enter valid start date' },
      end_date: { value: '', error: false, errorMessage: 'Please enter valid end date' },
      no_of_tokens: { value: '', error: false, errorMessage: 'Please enter a valid number of Tips' },
      bonus_type: { value: '', error: false, errorMessage: 'Please enter a valid number of Tips' },
      token_category_details: { value: '', error: false, errorMessage: 'Please enter a valid number of Tips' },
    });
    setSelectedValue1('');
    setSendPush(true);
    setEditingId(null);
  };

  const openAddDrawer = () => { resetForm(); setDrawerOpen(true); };
  const closeDrawer = () => { setDrawerOpen(false); resetForm(); };

  const fetchBonus = async () => {
    const jwtToken = sessionStorage.getItem('jwtToken');
    if (!jwtToken) return;
    dispatch(loaderOn());
    try {
      const res = await getBonus();
      const list = Array.isArray(res) ? res : (res?.content ?? res?.data ?? res?.items ?? []);
      setRows1(list.map((r: any) => ({
        bonus_id: r.bonus_id,
        bonus_name: r.bonus_name,
        categoryDetail: sousTypeVise(r),
        no_of_tokens: r.no_of_tokens,
        start_date: r.start_date,
        end_date: r.end_date,
        bonus_type: r.bonus_type,
        winnerEmail: r.winnerEmail ?? null,
      })));
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, "Historique des concours illisible"), 'red'));
    } finally {
      dispatch(loaderOff());
    }
  };

  const fetchBonus1 = async () => {
    const jwtToken = sessionStorage.getItem('jwtToken');
    if (!jwtToken) return;
    dispatch(loaderOn());
    try {
      const bonusRes = await getActiveBonus();
      const list = Array.isArray(bonusRes) ? bonusRes : (bonusRes?.content ?? bonusRes?.data ?? bonusRes?.items ?? []);
      setRows(list.map((r: any) => ({
        bonus_id: r.bonus_id,
        bonus_name: r.bonus_name,
        categoryDetail: sousTypeVise(r),
        no_of_tokens: r.no_of_tokens,
        raw_start_date: r.start_date,
        raw_end_date: r.end_date,
        start_date: r.start_date,
        end_date: r.end_date,
      })));
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, "Concours en cours illisibles"), 'red'));
    } finally {
      dispatch(loaderOff());
    }
  };

  const fetchAll = () => {
    fetchBonus();
    fetchBonus1();
  };

  const getRoleData = async () => {
    try {
      const res: any = await getUser();
      setBonusWrite(res?.role_type?.bonus_write ?? false);
    } catch (_) {
      dispatch(openSnackbar("Droits de l'administrateur illisibles : création et modification des concours masquées", 'red'));
    }
  };

  useEffect(() => {
    fetchAll();
    getRoleData();
  }, []);

  const handleSendPushForBoost = async (row: ActiveRow) => {
    if (!window.confirm(`Envoyer le push du concours « ${row.bonus_name} » à TOUS les Spotters ?`)) return;
    setSendingPush((p) => ({ ...p, [row.bonus_id]: true }));
    try {
      const bilan = await envoyerPushConcours(row.bonus_id);
      setPushConfirm((p) => ({ ...p, [row.bonus_id]: bilan.message }));
      setTimeout(() => setPushConfirm((p) => { const n = { ...p }; delete n[row.bonus_id]; return n; }), 5000);
    } catch (e) {
      dispatch(openSnackbar(messageServeur(e, "Erreur lors de l'envoi du push"), 'red'));
    } finally {
      setSendingPush((p) => ({ ...p, [row.bonus_id]: false }));
    }
  };

  const handleAddBonus = async () => {
    const jwtToken = sessionStorage.getItem('jwtToken');
    if (!jwtToken) { dispatch(openSnackbar('Session expirée', 'red')); return; }
    if (!formValues.bonus_name.value.trim()) {
      dispatch(openSnackbar('Le nom du concours est obligatoire', 'red')); return;
    }
    if (!formValues.start_date.value) {
      dispatch(openSnackbar('La date de début est obligatoire', 'red')); return;
    }
    if (!formValues.end_date.value) {
      dispatch(openSnackbar('La date de fin est obligatoire', 'red')); return;
    }
    if (new Date(formValues.end_date.value) <= new Date(formValues.start_date.value)) {
      dispatch(openSnackbar('La date de fin doit être postérieure à la date de début', 'red')); return;
    }
    const tipsGagnant = Number(formValues.no_of_tokens.value);
    if (!Number.isInteger(tipsGagnant) || tipsGagnant < 1) {
      dispatch(openSnackbar('Les Tips du gagnant sont un nombre entier, au moins 1', 'red')); return;
    }
    const formData = {
      bonus_name: formValues.bonus_name.value,
      bonus_description: formValues.bonus_description.value,
      bonus_criteria: formValues.bonus_criteria.value,
      start_date: formatDateString(formValues.start_date.value),
      end_date: formatDateString(formValues.end_date.value),
      no_of_tokens: tipsGagnant,
      delete_status: 'NO',
      bonus_type: 'Multiplicateur',
      categoryDetail: selectedValue1,
      /* Identifiant du référentiel (Z2-22) : le concours joint les POI par lui. */
      categoryDetailId: idSousTypeUnique(taxonomie, selectedValue1) ?? null,
    };
    dispatch(loaderOn());
    let cree: any;
    try {
      cree = await addBonus(formData);
    } catch (err) {
      dispatch(openSnackbar(messageConcours(err, 'Création du concours impossible'), 'red'));
      dispatch(loaderOff());
      return;
    }
    closeDrawer();
    fetchAll();
    /* Le concours est enregistré : un échec du push ne l'annule pas, mais
       doit se voir. */
    try {
      if (sendPush) {
        const bilan = await envoyerPushConcours(cree?.bonus_id);
        dispatch(openSnackbar(`Concours créé. ${bilan.message}`, 'green'));
      } else {
        dispatch(openSnackbar('Concours créé', 'green'));
      }
    } catch (e) {
      dispatch(openSnackbar(`Concours créé, mais push non envoyé : ${messageServeur(e, 'erreur inconnue')}`, 'red'));
    } finally {
      dispatch(loaderOff());
    }
  };

  const handleEdit = async (bonusId: number) => {
    const jwtToken = sessionStorage.getItem('jwtToken');
    if (!jwtToken) return;
    dispatch(loaderOn());
    try {
      const res = await getBonusId(bonusId);
      setFormValues((prev) => ({
        ...prev,
        bonus_name: { ...prev.bonus_name, value: res.bonus_name || '' },
        bonus_description: { ...prev.bonus_description, value: res.bonus_description || '' },
        bonus_criteria: { ...prev.bonus_criteria, value: res.bonus_criteria || '' },
        start_date: { ...prev.start_date, value: res.start_date || '' },
        end_date: { ...prev.end_date, value: res.end_date || '' },
        no_of_tokens: { ...prev.no_of_tokens, value: String(res.no_of_tokens || '1') },
        bonus_type: { ...prev.bonus_type, value: res.bonus_type || '' },
        token_category_details: { ...prev.token_category_details, value: sousTypeVise(res) },
      }));
      const sousType = sousTypeVise(res);
      if (sousType) {
        setSelectedValue1(sousType);
        compterPoi(sousType);
      }
      setEditingId(bonusId);
      setDrawerOpen(true);
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, 'Concours illisible'), 'red'));
    } finally {
      dispatch(loaderOff());
    }
  };

  const handleEditt = async (bonusId: number) => {
    const jwtToken = sessionStorage.getItem('jwtToken');
    if (!jwtToken) return;
    const tipsGagnant = Number(formValues.no_of_tokens.value);
    if (!Number.isInteger(tipsGagnant) || tipsGagnant < 1) {
      dispatch(openSnackbar('Les Tips du gagnant sont un nombre entier, au moins 1', 'red')); return;
    }
    const formData = {
      bonus_name: formValues.bonus_name.value,
      bonus_description: formValues.bonus_description.value,
      bonus_criteria: formValues.bonus_criteria.value,
      start_date: formatDateString(formValues.start_date.value),
      end_date: formatDateString(formValues.end_date.value),
      no_of_tokens: tipsGagnant,
      bonus_type: 'Multiplicateur',
      categoryDetail: selectedValue1,
      /* Identifiant du référentiel (Z2-22) : le concours joint les POI par lui. */
      categoryDetailId: idSousTypeUnique(taxonomie, selectedValue1) ?? null,
    };
    dispatch(loaderOn());
    try {
      await updateBonus(bonusId, formData);
      closeDrawer();
      fetchAll();
      dispatch(openSnackbar('Concours mis à jour', 'green'));
    } catch (err) {
      dispatch(openSnackbar(messageConcours(err, 'Mise à jour du concours impossible'), 'red'));
    } finally {
      dispatch(loaderOff());
    }
  };

  const handleDelete = async (bonusId: number) => {
    const jwtToken = sessionStorage.getItem('jwtToken');
    if (!jwtToken) return;
    dispatch(loaderOn());
    try {
      await updateBonusDelete(bonusId, { delete_status: 'Yes' });
      fetchAll();
      dispatch(openSnackbar('Concours supprimé', 'green'));
    } catch (err) {
      dispatch(openSnackbar(messageConcours(err, 'Suppression du concours impossible'), 'red'));
    } finally {
      dispatch(loaderOff());
    }
  };

  const now = Date.now();

  const tipsEnJeu = rows.reduce((acc, r) => acc + (parseFloat(r.no_of_tokens || '0') || 0), 0);

  const scheduled = rows.filter((r) => new Date(r.raw_start_date).getTime() > now).length;

  const kpis = [
    { label: 'Concours en cours', value: rows.length, color: C.accentLight, sub: 'ouverts ou planifiés' },
    { label: 'Tips en jeu', value: tipsEnJeu.toLocaleString('fr-FR'), color: C.warning, sub: 'un gagnant par concours' },
    { label: 'Planifiés', value: scheduled, color: '#1D5FA4', sub: 'démarrage futur' },
  ];

  return (
    <Box sx={{ p: 3, minHeight: '100vh', background: C.bg }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <TitrePage domaine="reseau" aide={AIDE_RESEAU.concours}>Concours</TitrePage>
          <Typography sx={{ fontSize: 13, color: C.textMuted, mt: 0.5 }}>
            Sur la période, le Spotter inscrit qui fait valider le plus de lieux du type ciblé gagne les Tips annoncés, une fois.
          </Typography>
        </Box>
        {bonus_write && (
          <Button
            startIcon={<AddIcon />}
            onClick={openAddDrawer}
            sx={{
              background: C.accent,
              color: '#fff', borderRadius: '8px', textTransform: 'none',
              fontWeight: 600, px: 3, py: 1.2,
              boxShadow: "none",
              '&:hover': { boxShadow: "none" },
            }}
          >
            Créer un concours
          </Button>
        )}
      </Box>

      {/* KPIs */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 4 }}>
        {kpis.map((k) => (
          <Box key={k.label} sx={{
            background: C.card, border: "1.5px solid #1F1E1B", borderRadius: '8px',
            p: 2.5, position: 'relative', overflow: 'hidden',
            '&::before': {
              content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
              background: k.color,
            },
          }}>
            <Typography sx={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
              {k.label}
            </Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, color: k.color, lineHeight: 1 }}>
              {k.value}
            </Typography>
            <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 0.5 }}>{k.sub}</Typography>
          </Box>
        ))}
      </Box>

      {/* Active boost cards */}
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.textSub, mb: 2, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Concours en cours
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 4 }}>
        {rows.map((row) => {
          const rawStart = new Date(row.raw_start_date).getTime();
          const rawEnd = new Date(row.raw_end_date).getTime();
          const expired = rawEnd < now;
          const pct = expired ? 100 : Math.max(0, Math.min(((now - rawStart) / (rawEnd - rawStart)) * 100, 100));
          const tipsGagnant = parseFloat(row.no_of_tokens || '0') || 0;
          const commence = aCommence(row.raw_start_date);
          const isSending = sendingPush[row.bonus_id] ?? false;
          const confirm = pushConfirm[row.bonus_id];

          return (
            <Box key={row.bonus_id} sx={{
              background: expired ? 'var(--c-surface)' : C.card,
              border: `1px solid ${expired ? 'rgba(31,30,27,0.04)' : C.border}`,
              borderRadius: '8px', p: 2.5,
              opacity: expired ? 0.7 : 1,
              transition: 'border-color 0.2s',
              '&:hover': { borderColor: expired ? 'rgba(31,30,27,0.06)' : C.borderAccent },
            }}>
              {/* Card header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ flex: 1, mr: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.text }}>{row.bonus_name}</Typography>
                    {/* Tips au gagnant */}
                    <Box sx={{
                      background: `${C.warning}22`,
                      border: `1px solid ${C.warning}55`,
                      color: C.warning,
                      borderRadius: '6px', px: 1, py: 0.25,
                      fontSize: 11, fontWeight: 700,
                    }}>
                      🏆 {tipsGagnant.toLocaleString('fr-FR')} Tips au gagnant
                    </Box>
                    {/* Expiré */}
                    {expired && (
                      <Box sx={{
                        background: 'rgba(31,30,27,0.06)', color: C.textMuted,
                        borderRadius: '6px', px: 1, py: 0.25, fontSize: 10, fontWeight: 600,
                      }}>
                        Expiré
                      </Box>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 12, color: C.textMuted, mt: 0.25 }}>{row.categoryDetail}</Typography>
                </Box>
                {bonus_write && (
                  <Tooltip title={commence ? NON_MODIFIABLE : ''}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" disabled={commence} onClick={() => handleEdit(row.bonus_id)} sx={{ color: C.accentLight, '&:hover': { background: C.accentMuted }, '&.Mui-disabled': { color: C.textMuted } }}>
                        <CreateIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" disabled={commence} onClick={() => { setDeleteTargetId(row.bonus_id); setOpenDialog(true); }} sx={{ color: C.error, '&:hover': { background: 'rgba(162,58,13,0.1)' }, '&.Mui-disabled': { color: C.textMuted } }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Tooltip>
                )}
              </Box>

              {/* Progress bar */}
              <Box sx={{ height: '4px', borderRadius: '2px', background: 'rgba(31,30,27,0.06)', mt: 1.5, mb: 1, overflow: 'hidden' }}>
                <Box sx={{
                  height: '100%',
                  width: `${pct}%`,
                  background: expired ? 'rgba(31,30,27,0.15)' : C.accent,
                  borderRadius: '2px',
                  transition: 'width 0.5s ease',
                }} />
              </Box>

              {/* Countdown */}
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: 10, color: C.textMuted, mb: 0.25 }}>Temps restant</Typography>
                <Countdown endDate={row.raw_end_date} />
              </Box>

              {/* Push button */}
              {!expired && (
                <Box>
                  {confirm ? (
                    <Box sx={{ background: `${C.success}15`, border: `1px solid ${C.success}33`, borderRadius: '8px', px: 2, py: 1 }}>
                      <Typography sx={{ fontSize: 12, color: C.success, fontWeight: 600 }}>{confirm}</Typography>
                    </Box>
                  ) : (
                    <Button
                      size="small"
                      startIcon={<NotificationsIcon sx={{ fontSize: 14 }} />}
                      disabled={isSending}
                      onClick={() => handleSendPushForBoost(row)}
                      sx={{
                        width: '100%',
                        color: C.accentLight, border: `1px solid ${C.borderAccent}`,
                        borderRadius: '8px', textTransform: 'none', fontSize: 12, fontWeight: 600,
                        py: 0.75,
                        '&:hover': { background: C.accentMuted },
                        '&.Mui-disabled': { color: C.textMuted, borderColor: C.border },
                      }}
                    >
                      {isSending ? 'Envoi...' : 'Lancer le push'}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          );
        })}

        {/* Create card */}
        {bonus_write && (
          <Box onClick={openAddDrawer} sx={{
            border: '2px dashed rgba(31,30,27,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minHeight: 220, cursor: 'pointer', borderRadius: '8px',
            transition: 'border-color 0.2s, background 0.2s',
            '&:hover': { borderColor: C.borderAccent, background: C.accentMuted },
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <AddIcon sx={{ fontSize: 32, color: C.textMuted, mb: 1 }} />
              <Typography sx={{ fontSize: 13, color: C.textMuted, fontWeight: 600 }}>+ Créer un concours</Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* History section */}
      <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: '8px', overflow: 'hidden' }}>
        <Box
          onClick={() => setShowHistory((p) => !p)}
          sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: 3, py: 2, cursor: 'pointer',
            '&:hover': { background: 'rgba(31,30,27,0.02)' },
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.textSub }}>
            Voir l'historique ({rows1.length})
          </Typography>
          <Typography sx={{ fontSize: 12, color: C.textMuted }}>{showHistory ? '▲' : '▼'}</Typography>
        </Box>
        {showHistory && (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Nom', 'Type de lieu', 'Tips au gagnant', 'Début', 'Fin', 'Gagnant', 'Actions'].map((h) => (
                      <TableCell key={h} sx={{ background: C.card, color: C.accentLight, fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: `1px solid ${C.border}` }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows1.slice(histPage * histRowsPerPage, histPage * histRowsPerPage + histRowsPerPage).map((r) => {
                    const commence = aCommence(r.start_date);
                    return (
                    <TableRow key={r.bonus_id} sx={{ '& .MuiTableCell-root': { color: C.text, borderBottom: `1px solid ${C.border}`, fontSize: '13px' }, '&:hover': { background: 'rgba(31,30,27,0.02)' } }}>
                      <TableCell>{r.bonus_name}</TableCell>
                      <TableCell><Typography sx={{ fontSize: 12, color: C.textMuted }}>{r.categoryDetail}</Typography></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'inline-block', background: C.accentMuted, color: C.accentLight, borderRadius: '6px', px: 1, py: 0.25, fontSize: 12, fontWeight: 700 }}>
                          {r.no_of_tokens} Tips
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: C.textMuted }}>{r.start_date}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: C.textMuted }}>{r.end_date}</TableCell>
                      <TableCell>
                        {r.winnerEmail ? (
                          <Typography sx={{ fontSize: 12, color: C.success, fontWeight: 600 }}>🏆 {masquerEmail(r.winnerEmail)}</Typography>
                        ) : (
                          <Typography sx={{ fontSize: 12, color: C.textMuted }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {bonus_write && (
                          <Tooltip title={commence ? NON_MODIFIABLE : ''}>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton size="small" disabled={commence} onClick={() => handleEdit(r.bonus_id)} sx={{ color: C.accentLight, '&.Mui-disabled': { color: C.textMuted } }}>
                                <CreateIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                              <IconButton size="small" disabled={commence} onClick={() => { setDeleteTargetId(r.bonus_id); setOpenDialog(true); }} sx={{ color: C.error, '&.Mui-disabled': { color: C.textMuted } }}>
                                <DeleteIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  {rows1.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: C.textMuted, borderBottom: 'none' }}>
                        Aucune donnée
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', px: 2, py: 1, borderTop: `1px solid ${C.border}` }}>
              <Typography sx={{ fontSize: 12, color: C.textMuted, mr: 2 }}>
                {histPage * histRowsPerPage + 1}–{Math.min((histPage + 1) * histRowsPerPage, rows1.length)} sur {rows1.length}
              </Typography>
              <Button size="small" disabled={histPage === 0} onClick={() => setHistPage((p) => p - 1)} sx={{ color: C.textMuted, minWidth: 32 }}>‹</Button>
              <Button size="small" disabled={(histPage + 1) * histRowsPerPage >= rows1.length} onClick={() => setHistPage((p) => p + 1)} sx={{ color: C.textMuted, minWidth: 32 }}>›</Button>
            </Box>
          </>
        )}
      </Box>

      {/* Delete confirm dialog */}
      <CommonDialog
        open={openDialog}
        handleClose={() => setOpenDialog(false)}
        handleConfirm={() => { if (deleteTargetId !== null) handleDelete(deleteTargetId); setOpenDialog(false); }}
        contentText="Supprimer ce concours ? Seul un concours pas encore commencé peut l'être."
      />

      {/* Add/Edit Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer} PaperProps={{ sx: { width: 440, background: C.card, borderLeft: `1px solid ${C.border}` } }}>
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Drawer header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.text }}>
                {editingId ? 'Modifier le concours' : 'Créer un concours'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: C.textMuted, mt: 0.25 }}>
                Le Spotter inscrit qui fait valider le plus de lieux du type ciblé gagne les Tips, une fois
              </Typography>
            </Box>
            <IconButton onClick={closeDrawer} sx={{ color: C.textMuted }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Titre */}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.textSub, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Titre du concours
              </Typography>
              <TextField
                fullWidth size="small"
                placeholder="Ex: Concours Pharmacie Dakar"
                name="bonus_name"
                value={formValues.bonus_name.value}
                onChange={(e) => setFormValues((prev) => ({ ...prev, bonus_name: { ...prev.bonus_name, value: e.target.value } }))}
                sx={fieldSx}
              />
            </Box>

            {/* Catégorie */}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.textSub, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Type de lieu ciblé
              </Typography>
              <Select
                native
                fullWidth
                size="small"
                value={selectedValue1}
                onChange={(e) => {
                  const val = e.target.value as string;
                  setSelectedValue1(val);
                  compterPoi(val);
                }}
                sx={{
                  ...selectSx,
                  '& select': { color: selectedValue1 ? C.text : C.textMuted, background: 'transparent' },
                  '& option': { background: 'var(--c-surface)', color: C.text },
                  '& optgroup': { background: 'var(--c-surface-2)', color: C.accentLight, fontStyle: 'normal', fontWeight: 700 },
                }}
              >
                <option value="" disabled>Sélectionner un sous-type de lieu</option>
                {taxonomie.map((cat) => (
                  <optgroup key={cat.libelle} label={cat.libelle}>
                    {cat.sousTypes.map((sousType) => (
                      <option key={sousType} value={sousType}>{sousType}</option>
                    ))}
                  </optgroup>
                ))}
              </Select>
              {/* VOIR AVANT DE LANCER QU'UN BONUS EST VOUÉ À L'ÉCHEC.
                  Le vrai problème n'était pas qu'on ne voyait pas l'échec à la
                  fin, c'est qu'on ne voyait pas qu'il était écrit d'avance :
                  deux bonus ont tourné à vide quatre mois sur des libellés que
                  zéro POI ne portait. */}
              {selectedValue1 && (
                <Typography sx={{
                  fontSize: 11, mt: 0.75, lineHeight: 1.6,
                  color: poiCibles === null ? C.textMuted : poiCibles === 0 ? C.error : C.textSub,
                }}>
                  {poiCibles === null
                    ? "Comptage des lieux correspondants…"
                    : poiCibles === 0
                      ? `⚠ Aucun lieu ne porte « ${selectedValue1} » aujourd'hui. Ce concours ne désignera aucun gagnant.`
                      : `${poiCibles} ${poiCibles > 1 ? "lieux portent" : "lieu porte"} « ${selectedValue1} » — ${collecteursCibles} Spotter(s) distinct(s).`}
                </Typography>
              )}
            </Box>

            {/* Tips au gagnant */}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.textSub, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Tips au gagnant
              </Typography>
              <TextField
                fullWidth size="small" type="number"
                inputProps={{ min: 1, step: 1 }}
                placeholder="Ex: 500"
                value={formValues.no_of_tokens.value}
                onChange={(e) => setFormValues((prev) => ({ ...prev, no_of_tokens: { ...prev.no_of_tokens, value: e.target.value } }))}
                sx={fieldSx}
              />
              <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 0.5 }}>
                Crédités une seule fois, au gagnant, à la clôture du concours.
              </Typography>
            </Box>

            {/* Dates */}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.textSub, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Période
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={['DateTimePicker', 'DateTimePicker']}>
                  <DateTimePicker
                    label="Date de début"
                    value={formValues.start_date.value ? dayjs(formValues.start_date.value) : null}
                    onChange={(val) => setFormValues((prev) => ({ ...prev, start_date: { ...prev.start_date, value: val ? val.toISOString() : '' } }))}
                    slotProps={{
                      textField: {
                        size: 'small', fullWidth: true, sx: {
                          ...fieldSx,
                          '& .MuiSvgIcon-root': { color: C.textMuted },
                        },
                      },
                    }}
                  />
                  <DateTimePicker
                    label="Date de fin"
                    value={formValues.end_date.value ? dayjs(formValues.end_date.value) : null}
                    onChange={(val) => setFormValues((prev) => ({ ...prev, end_date: { ...prev.end_date, value: val ? val.toISOString() : '' } }))}
                    slotProps={{
                      textField: {
                        size: 'small', fullWidth: true, sx: {
                          ...fieldSx,
                          '& .MuiSvgIcon-root': { color: C.textMuted },
                        },
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Box>

            {/* Push à la création seulement : la modification n'envoie rien */}
            {!editingId && (
            <Box sx={{ background: 'rgba(31,30,27,0.03)', borderRadius: '8px', p: 1.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={sendPush}
                    onChange={(e) => setSendPush(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: C.accentLight },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { background: C.accentLight },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 12, color: C.textSub }}>
                    Notifier tous les Spotters actifs dès l'enregistrement du concours
                  </Typography>
                }
              />
            </Box>
            )}

            {/* Preview */}
            {(parseFloat(formValues.no_of_tokens.value) > 0 || selectedValue1) && (
              <Box sx={{ background: C.accentMuted, border: `1px solid ${C.borderAccent}`, borderRadius: '8px', p: 2 }}>
                <Typography sx={{ fontSize: 11, color: C.accentLight, fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Aperçu
                </Typography>
                <Typography sx={{ fontSize: 13, color: C.text }}>
                  Le Spotter inscrit qui fera valider le plus de lieux{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: C.text }}>
                    « {selectedValue1 || 'du type sélectionné'} »
                  </Box>{' '}
                  sur la période gagnera{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: C.accentLight }}>
                    {parseFloat(formValues.no_of_tokens.value) || 0} Tips
                  </Box>, une fois.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Drawer actions */}
          <Box sx={{ display: 'flex', gap: 2, pt: 3, borderTop: `1px solid ${C.border}`, mt: 2 }}>
            <Button
              fullWidth onClick={closeDrawer}
              sx={{ color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', textTransform: 'none', fontWeight: 600, '&:hover': { background: 'rgba(31,30,27,0.04)' } }}
            >
              Annuler
            </Button>
            <Button
              fullWidth
              onClick={() => { if (editingId) handleEditt(editingId); else handleAddBonus(); }}
              sx={{
                background: C.accent,
                color: '#fff', borderRadius: '8px', textTransform: 'none', fontWeight: 600,
                boxShadow: "none",
                '&:hover': { boxShadow: "none" },
              }}
            >
              {editingId ? 'Mettre à jour' : 'Enregistrer'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
